// React
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

// Components
import BCartPicker from 'ui/blocks/Picker/types/cart'
import { LocationPicker } from 'ui/components/Picker/LocationPicker'
import { DeliveryFulfillmentForm } from './DeliveryFulfillmentForm'
import Div from 'ui/elements/Div'
import Label from 'ui/elements/Label'
import Span from 'ui/elements/Span'

// Libraries
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation as farTriangleExclamation } from '@fortawesome/pro-regular-svg-icons'

// Hooks
import { useFormikContext } from 'formik'
import { useOrm } from 'shared/utils/useOrm'
import { useDeliveryOptions } from './hooks'
import { useTranslation } from 'shared/utils/useTranslation'
import { useSearchParam } from 'shared/utils/useSearchParam'
import { useSettings } from 'shared/utils/useSettings'

// Shared
import { Field } from 'shared/form'

export const FulfillmentType = ({ cart, fetchAvailability }) => {
  const [deliveryError, setDeliveryError] = useState(null)

  const form = useFormikContext()
  const fulfillmentTab = useSearchParam('fulfillment-tab')
  const { tOrEmptyString, t } = useTranslation('user')

  const pickupLocations = useOrm('locations', (locations) => {
    return locations
      .getAll({ archived_at: [null, undefined], location_type: 'rental' })
      .filter((location) => location.fulfillment_capabilities.includes('pickup'))
  })
  const deliveryLocations = useOrm('locations', (locations) => {
    return locations
      .getAll({ archived_at: [null, undefined] })
      .filter((location) => location.fulfillment_capabilities.includes('delivery'))
  })

  const requireDeliveryAddress = useSettings('store.require_delivery_address_when_selecting_rental_period')

  useDeliveryOptions({ cart, fetchAvailability, onError: setDeliveryError, resetError: () => setDeliveryError(null) })

  const showFulfillmentTypeSelect = !!pickupLocations.size() && !!deliveryLocations.size()
  const isPickup = form.values.data.fulfillment_type === 'pickup'
  const isDelivery = form.values.data.fulfillment_type === 'delivery'

  useEffect(() => {
    if (fulfillmentTab === 'delivery') {
      form.setFieldValue('data.fulfillment_type', 'delivery')
    }
  }, [])

  // Auto-set fulfillment type when only one option is available
  useEffect(() => {
    const hasOnlyDelivery = !pickupLocations.size() && !!deliveryLocations.size()
    const hasOnlyPickup = !!pickupLocations.size() && !deliveryLocations.size()

    if (hasOnlyDelivery && !isDelivery) {
      form.setFieldValue('data.fulfillment_type', 'delivery')
    } else if (hasOnlyPickup && !isPickup) {
      form.setFieldValue('data.fulfillment_type', 'pickup')
    }
  }, [pickupLocations.size(), deliveryLocations.size(), isPickup, isDelivery])

  useEffect(() => {
    const fulfillmentType = form.values.data.fulfillment_type

    if (fulfillmentType === 'delivery' && !form.values.delivery_address?.country && !form.values.delivery_address?.country_id && requireDeliveryAddress) {
      form.setFieldError(
        'delivery_address.country',
        `${tOrEmptyString('user.deliveries.address_fields.country')} ${tOrEmptyString('user.public_errors.invalid')}`
      )
    }

    if (fulfillmentType === 'pickup' || !requireDeliveryAddress) {
      const errors = { ...form.errors }

      delete errors.delivery_address

      form.setErrors(errors)
    }
  }, [form.values, form.errors, requireDeliveryAddress])

  const handleLocationChange = ({ start_location_id: id }) => {
    const values = {
      ...form.values,
      start_location_id: id
    }

    fetchAvailability({ type: 'start', values })
  }

  const handleFulfillmentTypeChange = (value) => {
    if (form.values.data.fulfillment_type === value) return

    form.setFieldValue('data.fulfillment_type', value)

    switch (value) {
      case 'pickup': {
        const location = pickupLocations.first()

        if (!location) return

        form.setFieldValue('start_location_id', location.id)
        form.setFieldValue('stop_location_id', location.id)

        form.validateForm({
          ...form.values,
          start_location_id: location.id,
          stop_location_id: location.id
        })

        fetchAvailability({
          type: 'start',
          values: {
            ...form.values,
            start_location_id: location.id
          }
        })

        break
      }
      case 'delivery': {
        const location = deliveryLocations.first()

        if (!location) return

        form.setFieldValue('start_location_id', location.id)
        form.setFieldValue('stop_location_id', location.id)

        form.validateForm({
          ...form.values,
          start_location_id: location.id,
          stop_location_id: location.id
        })

        if (requireDeliveryAddress) return

        fetchAvailability({
          type: 'start',
          values: {
            ...form.values,
            start_location_id: location.id
          }
        })

        break
      }
    }
  }

  return (
    <BCartPicker.Section>
      <Div modifiers="display-flex flex-column gap-4 width-100p">
        {!!deliveryError && (
          <BCartPicker.Error>
            <FontAwesomeIcon icon={farTriangleExclamation} />
            <Span modifiers="font-size-md color-Warning/Foreground1 font-weight-semibold">
              {t(`user.deliveries.${deliveryError}`)}
            </Span>
          </BCartPicker.Error>
        )}
        {showFulfillmentTypeSelect && (
          <BCartPicker.FulfillmentTypeSelect>
            <Field
              name="data.fulfillment_type"
              type="radioGroup"
              options={[
                {
                  label: t('user.deliveries.pickup'),
                  value: 'pickup'
                },
                {
                  label: t('user.deliveries.delivery'),
                  value: 'delivery'
                }
              ]}
              modifiers={{
                inputModifiers: {
                  radioGroup: 'no-padding border-radius-md',
                  radioGroupIcon: 'margin-left-md margin-top-sm',
                  radioGroupLabel: 'padding-sm padding-left-xxl'
                }
              }}
              data-tid="Fulfillment type select"
              onChange={handleFulfillmentTypeChange}
            />
          </BCartPicker.FulfillmentTypeSelect>
        )}
        {isPickup && pickupLocations.size() > 1 && (
          <Div data-tid="Pickup fulfillment type form">
            <Label>{t('user.store.start_and_stop_location')}</Label>
            <BCartPicker.Section disableStyling>
              <LocationPicker type="cart" locations={pickupLocations} onChange={handleLocationChange} />
            </BCartPicker.Section>
          </Div>
        )}
        {isDelivery && requireDeliveryAddress && (
          <Div data-tid="Delivery fulfillment type form">
            <DeliveryFulfillmentForm />
          </Div>
        )}
      </Div>
    </BCartPicker.Section>
  )
}

FulfillmentType.propTypes = {
  cart: PropTypes.object,
  fetchAvailability: PropTypes.func.isRequired
}
